import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";

import { UseUsersDictionary, buildYetiScoresForKpis, windowUsesRangeYeti, yetiBucketForWindow, yetiInputsForWindow } from "./company";
import { filterKpisForPublicDisplay, filterLiveKpis, isKpiScorekeeperOnly } from "../constants/kpi";
import { ptsPerAmtOf, recomputeEntryPts, recomputeScopedPlayerScores } from "../helpers/kpiPoints";
import { finalizeClosedMonthSlice } from "../helpers/historicMonthBoard";
import { effectiveMetricColumns, hasMetricPlan, poolColumn, sourceWindowOf, broadestPlanWindow } from "../helpers/metricPlan";
import { fetchWorkspaceScores, monthsForWindow, monthsForMetricPlan, resolveWindow } from "../pages/CompanyMetrics/dashboards/useWorkspaceData";
import { kindOfKpi, rawTargetOf } from "../helpers/kpiTargetScale";
import { WINDOWS, windowLabel } from "../helpers/scopedWindows";
import { buildCompanyScoresByDay, isExcludedFromBoards } from "./kpi";
import {
  countsTowardAverage,
  effectiveMetricAmt,
  foldDayKpis,
  isBidAccuracyKpi,
  metaByIdFromDefs,
  metricCellScored,
  sortDaysAsc,
  backfillMonthPinnedRates,
} from "../helpers/scoreAggregate";
import {
  needsWindowRates,
  boundsForWindow,
  fetchWindowRates,
  overlayWindowRatesOnSlice,
} from "../helpers/windowRates";

/**
 * Scoped metrics — the shared data layer behind every component that can be
 * pointed at workspaces and dates.
 *
 * Three questions, asked identically by every card:
 *
 *   SCOPE   which workspaces feed it
 *   WINDOW  which dates  (this month · this quarter · YTD · a typed range)
 *   SHOW    average per player, or the team total
 *
 * The average follows what the app has always done rather than inventing a new
 * rule: `UseMyKpis` shows an admin the workspace AVERAGE PER PLAYER, dividing by
 * the players who actually scored on that metric — a rep with no estimates used
 * to count as a zero and dragged Closed Rate from 48% to 32%. Same divisor here.
 *
 *   one workspace   → that workspace's average per scoring player
 *   several ("All") → the average OF those averages, so a large team does not
 *                     outvote a small one when the question is company-wide
 *                     (The Yeti uses the same avg/total rule — never the sum of
 *                     every workspace's KPI paces while the team is averaged)
 *
 * With several workspaces only the metrics they ALL track can appear. Averaging
 * a number half the room does not measure would be a number that means nothing.
 */

/* Metric ids are per-workspace, so a shared metric is matched on its name. */
const nameKey = (k) => (k?.title || k?.name || "").trim().toLowerCase();

/* Scorekeeper-only metrics and rollups (Total Revenue, Team Hours) are kept
   out of every scoped tile (Beat the Yeti, KOTM, custom cards) the same way
   TVLeaderboard keeps them off the scoreboard — `defsAll` above still scores
   them so their points count toward totals, this list is just what renders.
   Both exclusions run BEFORE filterLiveKpis' 5-slot cap, not after — cutting
   a scorekeeper-only metric from an already-capped list of 5 just leaves a
   hole (the workspace shows 4 tiles instead of 5) instead of letting the
   next-ranked live metric backfill the slot. */
const liveKpisOf = (defs) =>
  filterLiveKpis(
    filterKpisForPublicDisplay((defs || []).filter((k) => k && !k.isDraft && !k.disabled && nameKey(k) && !isExcludedFromBoards(k))),
  );

// Re-exported so existing imports from this hook keep working.
export { WINDOWS, windowLabel, resolveWindow };

/**
 * One workspace's numbers inside a closed window.
 * Bounded on BOTH ends, so a typed range means exactly what it says.
 * Rate KPIs keep the latest day in the window (do not sum 0–1 snapshots).
 *
 * Points are recomputed from amt + KPI config (same rule as LiveLeaderboard) —
 * raw dailyScoreViews.pts drift (e.g. Estimate Won Value) must not inflate
 * KOTM / Beat the Yeti when the workspace board shows honest week totals.
 */
const readWorkspace = (scores, from, to, metaById = null, scoringKpis = null) => {
  const perUser = {};
  const kpiList = Array.isArray(scoringKpis) ? scoringKpis : null;
  (scores || []).forEach((score) => {
    const ownerId = score.ownerId || score.userId || null;
    if (!ownerId) return;
    const allDays = sortDaysAsc(score.days);
    const days = allDays.filter((day) => {
      const d = moment(day.day);
      if (!d.isValid()) return false;
      return !d.isBefore(from, "day") && !d.isAfter(to, "day");
    });
    /* Still build a row when only month-pinned rates exist outside the window
       (Today / mid-month week with Closed Rate on the 1st). */
    if (!days.length && !allDays.length) return;
    if (!perUser[ownerId]) perUser[ownerId] = { kpis: {}, pts: 0 };
    days.forEach((day) => {
      foldDayKpis(perUser[ownerId].kpis, day.kpis, metaById);
    });
    backfillMonthPinnedRates(perUser[ownerId].kpis, allDays, from, to, metaById);
    if (!days.length && !Object.keys(perUser[ownerId].kpis).length) {
      delete perUser[ownerId];
      return;
    }
    if (kpiList && kpiList.length) {
      const shaped = { total: 0 };
      Object.entries(perUser[ownerId].kpis).forEach(([id, cell]) => {
        shaped[id] = {
          amt: Number(cell?.amt) || 0,
          pts: Number(cell?.pts) || 0,
        };
      });
      const recomputed = recomputeScopedPlayerScores(shaped, kpiList);
      const nextKpis = {};
      Object.entries(recomputed).forEach(([id, cell]) => {
        if (id === "total" || !cell || typeof cell !== "object") return;
        nextKpis[id] = cell;
      });
      perUser[ownerId].kpis = nextKpis;
      perUser[ownerId].pts = Number(recomputed.total) || 0;
    } else {
      perUser[ownerId].pts = Object.values(perUser[ownerId].kpis)
        .reduce((s, c) => s + (Number(c.pts) || 0), 0);
    }
  });

  const byKpi = {};
  Object.values(perUser).forEach((u) => {
    Object.entries(u.kpis).forEach(([kpiId, cell]) => {
      /* Skip unscored / 0 placeholders — they were dragging Inside Sales
         Closed Rate to ~1% while Vern/Justin/Ben on the leaderboard were fine. */
      if (!countsTowardAverage(cell)) return;
      if (!byKpi[kpiId]) byKpi[kpiId] = { sum: 0, pts: 0, scorers: 0 };
      const def = kpiList
        ? kpiList.find((k) => String(k._id) === String(kpiId))
        : (metaById && (metaById[kpiId] || metaById[String(kpiId)])) || null;
      const pts = kpiList
        ? recomputeEntryPts(cell, def)
        : (Number(cell.pts) || 0);
      byKpi[kpiId].sum += Number(cell.amt) || 0;
      byKpi[kpiId].pts += pts;
      byKpi[kpiId].scorers += 1;
    });
  });
  Object.values(byKpi).forEach((o) => {
    o.avg = o.scorers ? o.sum / o.scorers : 0;
    o.avgPts = o.scorers ? o.pts / o.scorers : 0;
  });

  /* Bid Accuracy: no September row yet → perfect 100% for the window. */
  Object.entries(metaById || {}).forEach(([kpiId, meta]) => {
    if (!isBidAccuracyKpi(meta)) return;
    if (!byKpi[kpiId]) {
      byKpi[kpiId] = { sum: 1, pts: 0, scorers: 0, avg: 1, avgPts: 0, scored: false, defaultPerfect: true };
    } else if (!byKpi[kpiId].scored && (Number(byKpi[kpiId].sum) || 0) <= 0) {
      byKpi[kpiId].sum = 1;
      byKpi[kpiId].avg = 1;
      byKpi[kpiId].defaultPerfect = true;
    }
  });

  const ids = Object.keys(perUser);
  const teamPts = ids.reduce((n, id) => n + perUser[id].pts, 0);
  return {
    byKpi,
    players: ids.map((id) => ({ userId: id, pts: perUser[id].pts, kpis: perUser[id].kpis })),
    teamPts,
  };
};

/**
 * Live board roster only — archived / Yeti never count anywhere.
 * `isActive === false` is additionally excluded only when `strict` (the
 * default) — that rule exists to keep one inactive-but-not-archived player's
 * numbers from skewing an AVERAGE (KOTM, Beat the Yeti). A plain team TOTAL
 * (e.g. a "Team total" custom metric tile) has no averaging-fairness concern
 * and should match Scorekeeper/the server (isArchived only) instead of
 * silently dropping someone Scorekeeper still counts.
 */
const isLiveBoardPlayer = (usersDict, userId, { strict = true } = {}) => {
  if (!userId) return false;
  const u = usersDict?.[userId] || usersDict?.[String(userId)];
  /* Missing from the company roster: do NOT treat as live. An empty `{}`
     fallback used to pass every scorer (including archived Ben Ulbright) and
     inflate Head-of-Sales KOTM to raw ~54k on Analytics before users loaded. */
  if (!u || typeof u !== "object") return false;
  if (u.isArchived === true) return false;
  if (strict && u.isActive === false) return false;
  if (u.lastName === "Yeti" || u.isYeti) return false;
  return true;
};

/**
 * Drop archived (etc.) scorers from a workspace slice and rebuild byKpi /
 * teamPts so KOTM, Beat the Yeti, and metric tiles match the live roster.
 * `strict: false` (passed for "Team total" mode) skips the isActive-only
 * exclusion — see isLiveBoardPlayer.
 */
const dropArchivedFromSlice = (slice, usersDict, metaById = null, { strict = true } = {}) => {
  const players = (slice.players || []).filter((p) => isLiveBoardPlayer(usersDict, p.userId, { strict }));
  const byKpi = {};
  players.forEach((p) => {
    Object.entries(p.kpis || {}).forEach(([kpiId, cell]) => {
      if (!countsTowardAverage(cell)) return;
      if (!byKpi[kpiId]) byKpi[kpiId] = { sum: 0, pts: 0, scorers: 0 };
      byKpi[kpiId].sum += Number(cell.amt) || 0;
      byKpi[kpiId].pts += Number(cell.pts) || 0;
      byKpi[kpiId].scorers += 1;
    });
  });
  Object.values(byKpi).forEach((o) => {
    o.avg = o.scorers ? o.sum / o.scorers : 0;
    o.avgPts = o.scorers ? o.pts / o.scorers : 0;
  });
  Object.entries(metaById || {}).forEach(([kpiId, meta]) => {
    if (!isBidAccuracyKpi(meta)) return;
    if (!byKpi[kpiId]) {
      byKpi[kpiId] = { sum: 1, pts: 0, scorers: 0, avg: 1, avgPts: 0, scored: false, defaultPerfect: true };
    } else if (!byKpi[kpiId].scored && (Number(byKpi[kpiId].sum) || 0) <= 0) {
      byKpi[kpiId].sum = 1;
      byKpi[kpiId].avg = 1;
      byKpi[kpiId].defaultPerfect = true;
    }
  });
  const teamPts = players.reduce((n, p) => n + (Number(p.pts) || 0), 0);
  return { ...slice, players, byKpi, teamPts };
};

/**
 * @param {string[]} workspaceIds
 * @param {object} opts { window, custom:{from,to}, show:'avg'|'total' }
 */
export default function useScopedMetrics(workspaceIds, opts = {}) {
  const { window: win = "month", custom = null, show = "avg", plan = null } = opts;
  const planKey = hasMetricPlan(plan) ? JSON.stringify(plan) : "";
  const wide = hasMetricPlan(plan) ? broadestPlanWindow(plan, win === "year" ? "ytd" : win, custom) : { window: win, custom };
  const fetchWin = wide.window === "year" ? "ytd" : wide.window;
  const fetchCustom = wide.custom;
  const companyId = useSelector((s) => s.authReducer.userDetails?.companyId);
  const companyKpis = useSelector((s) => s.kpiReducer.companyKpis);
  const holidays = useSelector((s) => s.companyReducer.holidays);
  const usersDict = UseUsersDictionary();
  const allWorkspaces = useSelector((s) => s.workspacesReducer.workspaces);
  const wsNameById = useMemo(() => {
    const map = {};
    (allWorkspaces || []).forEach((w) => {
      if (w?._id) map[String(w._id)] = w.name || "Workspace";
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allWorkspaces]);

  const ids = useMemo(
    () => (workspaceIds || []).filter(Boolean).map(String),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [(workspaceIds || []).filter(Boolean).map(String).join("|")],
  );
  const customKey = custom ? `${custom.from || ""}~${custom.to || ""}` : "";
  /* companyKpis is the per-workspace metric catalog. Until the KPI saga fills
     it, scoped cards have definitions=[] and render host-looking zeros. Keep
     loading true so we don't paint empty tiles before the catalog is ready —
     visiting Sales used to "fix" this by forcing the saga to finish first. */
  const catalogReady = companyKpis != null;
  const planDriven = hasMetricPlan(plan);
  /* Metric-plan cards know their KPI ids — don't block the score fetch on the
     full company catalog (Analytics can mount before the KPI saga finishes). */
  const canFetch = catalogReady || (planDriven && ids.length > 0);
  /* Each scoped workspace must have a catalog entry (even []) before we trust
     recompute. `companyKpis != null` alone still left defsAll=[] briefly and
     KOTM settled on raw inflated pts (~54k Last week on Vercel). */
  const defsReady = planDriven || (
    companyKpis != null
    && ids.every((id) => Array.isArray(companyKpis[id]))
  );
  /* Company user list must be present so archived / inactive can be dropped.
     Before it arrives, every scorer looked "live" and Ben's raw pts stuck. */
  const rosterReady = !!(usersDict && Object.keys(usersDict).length > 0);
  /* Fetch key = months on the wire. Week↔month share one month payload — do NOT
     flip loading for a pure window change or the board sits on skeletons while
     we re-filter data we already have. */
  const monthsKey = (() => {
    const boardWin = fetchWin === "year" ? "ytd" : fetchWin;
    if (planDriven) return monthsForMetricPlan(plan, boardWin, fetchCustom).join(",");
    return monthsForWindow(boardWin, fetchCustom).join(",");
  })();
  const fetchKey = `${companyId || ""}|${ids.join(",")}|${monthsKey}|${canFetch ? "1" : "0"}`;

  const [scoresByWs, setScoresByWs] = useState({});
  const [loadedFetchKey, setLoadedFetchKey] = useState("");
  /* Window-rate overlay: { [wsId]: { [ownerId]: { [kpiId]: { amt, pts } } } } */
  const [windowRatesByWs, setWindowRatesByWs] = useState({});
  const ratesKey = `${ids.join(",")}|${fetchWin}|${customKey}|${planKey}`;
  const hasScores = Object.keys(scoresByWs).length > 0;
  const fetching = !!ids.length && (!canFetch || loadedFetchKey !== fetchKey);
  /* Full skeleton on cold start OR while roster/KPI defs are still hydrating —
     otherwise ScopedGameboard settles the climb on raw+archived totals. */
  const hydratePending = !!ids.length && hasScores && (!rosterReady || !defsReady);
  const loading = ((!canFetch && !!ids.length) || (fetching && !hasScores) || hydratePending);
  const refreshing = fetching && hasScores && !hydratePending;

  useEffect(() => {
    if (!companyId || !ids.length) {
      setScoresByWs({});
      setLoadedFetchKey(fetchKey);
      return undefined;
    }
    if (!canFetch) return undefined;
    let alive = true;
    const months = hasMetricPlan(plan)
      ? monthsForMetricPlan(plan, fetchWin === "year" ? "ytd" : fetchWin, fetchCustom)
      : monthsForWindow(fetchWin === "year" ? "ytd" : fetchWin, fetchCustom);
    Promise.all(
      ids.map((id) => fetchWorkspaceScores(companyId, id, months).then((s) => [id, s]).catch(() => [id, []])),
    ).then((pairs) => {
      if (!alive) return;
      setScoresByWs(Object.fromEntries(pairs));
      setLoadedFetchKey(fetchKey);
    });
    return () => { alive = false; };
  }, [companyId, ids, canFetch, fetchWin, customKey, fetchKey, planKey]);

  /* Closed Rate + Bid Accuracy for non-month windows (Today / Week / custom / …). */
  useEffect(() => {
    if (!companyId || !ids.length || !canFetch) {
      setWindowRatesByWs({});
      return undefined;
    }
    const cardWin = (win === "year" ? "ytd" : win) || "month";
    const needed = new Map();
    const addWin = (wsId, w, cust) => {
      if (!needsWindowRates(w)) return;
      const { from, to } = boundsForWindow(w, cust);
      if (!from || !to) return;
      needed.set(`${wsId}|${from}|${to}`, { groupId: wsId, from, to });
    };
    ids.forEach((id) => addWin(id, fetchWin, fetchCustom));
    if (hasMetricPlan(plan)) {
      (plan.cols || []).forEach((col) => {
        ids.forEach((id) => {
          const sw = sourceWindowOf(col, id, cardWin, custom);
          const srcWin = sw.window === "year" ? "ytd" : sw.window;
          addWin(id, srcWin, sw.custom);
        });
      });
    }
    if (!needed.size) {
      setWindowRatesByWs({});
      return undefined;
    }
    let alive = true;
    Promise.all(
      [...needed.values()].map(async ({ groupId, from, to }) => {
        const rates = await fetchWindowRates({ groupId, from, to });
        return [`${groupId}|${from}|${to}`, rates];
      }),
    ).then((pairs) => {
      if (!alive) return;
      /* Collapse to per-workspace using the board window key when possible;
         plan columns look up by full key below. */
      const byKey = Object.fromEntries(pairs);
      const byWs = {};
      ids.forEach((id) => {
        const { from, to } = boundsForWindow(fetchWin, fetchCustom);
        const primary = byKey[`${id}|${from}|${to}`];
        if (primary) byWs[id] = primary;
      });
      byWs.__byKey = byKey;
      setWindowRatesByWs(byWs);
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, ids, canFetch, ratesKey]);

  return useMemo(() => {
    if (!canFetch && ids.length) {
      return { kpis: [], kpisAll: [], yeti: null, yetiByWs: {}, people: [], teamPts: 0, from: null, to: null, seriesByDay: null, loading: true, workspaceCount: ids.length };
    }
    const { from, to } = resolveWindow(fetchWin, fetchCustom);

    const allDefsOf = (id) =>
      ((companyKpis && companyKpis[id]) || []).filter((k) => k && !k.isDraft && !k.disabled && nameKey(k));
    const ratesByKey = windowRatesByWs.__byKey || {};
    const perWs = ids.map((id) => {
      const defsAll = allDefsOf(id);
      const meta = metaByIdFromDefs(defsAll);
      /* Last month folds every day in the closed window (sum vs rate-aware via
         foldDayKpis), then recomputes points — same path as GetPreviousKpis /
         Live TV. Taking only the month's last day zeroed sum metrics. */
      let slice = readWorkspace(scoresByWs[id], from, to, meta, defsAll);
      if (fetchWin === "lastMonth") {
        slice = finalizeClosedMonthSlice(slice, defsAll, meta);
      }
      const boardRates = windowRatesByWs[id] || (
        needsWindowRates(fetchWin)
          ? ratesByKey[`${id}|${boundsForWindow(fetchWin, fetchCustom).from}|${boundsForWindow(fetchWin, fetchCustom).to}`]
          : null
      );
      if (boardRates) {
        slice = overlayWindowRatesOnSlice(slice, boardRates);
      }
      /* Archived / inactive never belong on KOTM or tile averages — Ben
         Ulbright alone was adding ~63k into the Sep 1–4 mountain average.
         "Team total" mode has no averaging-fairness concern, so it only
         drops archived players (matching Scorekeeper/the server) rather
         than also dropping isActive:false players an admin still sees
         logging real hours on Scorekeeper. */
      slice = dropArchivedFromSlice(slice, usersDict, meta, { strict: show !== "total" });
      return {
        id,
        defs: liveKpisOf((companyKpis && companyKpis[id]) || []),
        defsAll,
        ...slice,
      };
    });

    /* Progressive Scorekeeper targets are DAILY ("Memberships · 5 per day").
       Store the raw daily goal; CustomWidget scales it for the board window
       at paint time so every resolve path (plan / name / catalog) agrees. */
    const shapeKpi = (def) => {
      const key = nameKey(def);
      const each = perWs.map((w) => {
        const hit = w.defsAll.find((x) => nameKey(x) === key) || w.defs.find((x) => nameKey(x) === key);
        const cell = hit ? w.byKpi[String(hit._id)] : null;
        const avgRaw = cell?.avg || 0;
        const sumRaw = cell?.sum || 0;
        const avg = effectiveMetricAmt(hit, cell ? { ...cell, amt: avgRaw } : null, { missing: !cell });
        /* A percentage metric (Closed Rate, Bid Accuracy, any %-kind metric)
           has no valid "total" — summing two players' 98% and 100% Bid
           Accuracy gave 198%, which the display layer's <=1-is-a-fraction
           heuristic then misread as "1.98%" ("2%" on the tile). "Team total"
           mode for a % metric means the same thing "Average" does: the
           workspace's blended rate, never a sum of individual percentages.
           Scoped narrowly to %-kind (+ Bid Accuracy's title/calc match) so
           non-percentage metrics (composites, plain $/# sums) keep their
           existing "Team total" behavior unchanged. */
        const isPercentMetric = hit?.kind === "%" || isBidAccuracyKpi(hit);
        const sum = isPercentMetric ? avg : sumRaw;
        return { avg, avgPts: cell?.avgPts || 0, sum, pts: cell?.pts || 0, defaultPerfect: cell?.defaultPerfect };
      });
      /* Avg mode: only workspaces that actually scored — a 0 workspace must
         not dilute Closed Rate (Inside Sales was printing ~1%). Bid Accuracy
         defaults to 100% when unscored.
         Liveness is always judged on `avg` (the amount), never on the sign of
         whichever `pick` we're currently averaging. Anchor metrics can carry
         a genuinely negative avgPts (a workspace scoring below benchmark
         loses points), and filtering on "pts > 0" silently dropped that
         workspace from the points average while its (always non-negative
         rate) amount stayed in the amount average — the two cross-workspace
         averages ended up backed by different workspace sets, so a team
         sitting BELOW benchmark on the % tile could still show a positive
         points delta and paint the Yeti-comparison chip green. */
      const live = each.filter((w, wi) => {
        const ws = perWs[wi];
        const hit = ws.defsAll.find((x) => nameKey(x) === key) || ws.defs.find((x) => nameKey(x) === key);
        const cell = hit ? ws.byKpi[String(hit._id)] : null;
        if (cell?.defaultPerfect) return true;
        return (Number(w.avg) || 0) > 0;
      });
      const mean = (pick) => {
        if (!live.length) return 0;
        return live.reduce((a, w) => a + w[pick], 0) / live.length;
      };
      const total = (pick) => each.reduce((a, w) => a + w[pick], 0);
      const amount = show === "total" ? total("sum") : mean("avg");
      const stored = show === "total" ? total("pts") : mean("avgPts");
      const rate = Number(def?.pointValue) > 0 && Number(def?.pointScale) > 0 ? ptsPerAmtOf(def) : 0;
      const daily = rawTargetOf(def);
      /* Each workspace's OWN definition of this metric (benchmark, point
         rule, goal text) — a shared-by-name metric can be configured
         differently per workspace (Field Sales' Closed Rate benchmark isn't
         necessarily In-House's). The tile itself only shows one blended
         number, but the info tooltip needs every workspace's rule so it can
         say so instead of silently speaking for whichever workspace happened
         to be `def`. */
      const perWorkspace = perWs
        .map((w) => {
          const hit = w.defsAll.find((x) => nameKey(x) === key) || w.defs.find((x) => nameKey(x) === key);
          return hit
            ? {
              workspaceId: w.id,
              workspaceName: wsNameById[w.id] || "Workspace",
              def: hit,
              /* Reps who scored this metric in the window — what a Team total
                 is the sum of, so what the per-rep goal / Yeti scale by. */
              scorers: Number(w.byKpi[String(hit._id)]?.scorers) || 0,
            }
            : null;
        })
        .filter(Boolean);
      return {
        ...def,
        amount,
        points: rate ? amount * rate : stored,
        /* Keep Scorekeeper daily goal raw — CustomWidget scales for the board
           date filter at paint time so no resolve path can skip it. */
        _dailyTarget: daily,
        target: daily,
        _perWorkspace: perWorkspace,
        _workspaceCount: perWs.length,
      };
    };

    /* Several workspaces: only what they all track. `shared` is the ≤5 LIVE
       metrics (the Yeti tiles); `sharedAll` is every metric they have in common,
       so a custom component pointed at ANY metric still gets its team average. */
    const first = perWs[0];
    const shared = !first
      ? []
      : first.defs.filter((k) => perWs.every((w) => w.defs.some((x) => nameKey(x) === nameKey(k))));
    const sharedAll = !first
      ? []
      : first.defsAll.filter((k) => perWs.every((w) => w.defsAll.some((x) => nameKey(x) === nameKey(k))));

    /* When the card carries an explicit metric plan, its DISPLAY tiles are the
       plan's columns — differently-named metrics paired per workspace, pooled by
       each column's own sum/avg mode. `kpisAll` stays the full name-matched set
       so custom widgets can still resolve any metric by name. */
    const shapeColumn = (col) => {
      let repDef = null;
      /* "follow" sources must track the CARD/board window (`win`), not fetchWin.
         fetchWin is only widened so pinned sources (e.g. month) still load —
         using it here made Follow-dashboard columns silently stay on month. */
      const cardWin = (win === "year" ? "ytd" : win) || "month";
      const cells = perWs.map((w) => {
        const rawKpiId = col.sources?.[String(w.id)];
        const rawDef = rawKpiId
          ? (w.defsAll.find((x) => String(x._id) === String(rawKpiId))
            || w.defs.find((x) => String(x._id) === String(rawKpiId)) || null)
          : null;
        /* A saved Custom plan can point a column at a metric that has since
           been marked scorekeeper-only — that source must go dead (like a
           retired metric), not resurrect the metric onto this board. */
        const kpiId = isKpiScorekeeperOnly(rawDef) ? null : rawKpiId;
        const def = isKpiScorekeeperOnly(rawDef) ? null : rawDef;
        if (kpiId && !repDef && def) repDef = def;
        const sw = sourceWindowOf(col, w.id, cardWin, custom);
        const srcWin = sw.window === "year" ? "ytd" : sw.window;
        const { from: srcFrom, to: srcTo } = resolveWindow(srcWin, sw.custom);
        let wsSlice = readWorkspace(
          scoresByWs[w.id],
          srcFrom,
          srcTo,
          metaByIdFromDefs(w.defsAll),
          w.defsAll,
        );
        if (needsWindowRates(srcWin)) {
          const b = boundsForWindow(srcWin, sw.custom);
          const rates = ratesByKey[`${w.id}|${b.from}|${b.to}`];
          if (rates) wsSlice = overlayWindowRatesOnSlice(wsSlice, rates);
        }
        const cell = kpiId ? wsSlice.byKpi[String(kpiId)] : null;
        const missing = !cell;
        /* byKpi cells expose sum/avg, not amt — stamp amt before present/score
           checks or poolColumn drops every workspace and the card prints $0.
           A percentage source (Closed Rate, Bid Accuracy, any %-kind metric)
           has no valid "total" — same bug as shapeKpi: summing two players'
           98%/100% gives 198%, which the display layer misreads as "2%".
           Scoped narrowly to %-kind (+ Bid Accuracy) so non-percentage
           sources (composites, plain $/# sums) are unaffected. */
        const isPercentSource = def?.kind === "%" || isBidAccuracyKpi(def);
        let amtRaw = cell?.avg || 0;
        if (show === "total" && !isPercentSource) {
          amtRaw = cell?.sum || 0;
        }
        const scoredCell = cell ? { ...cell, amt: amtRaw } : null;
        const amt = effectiveMetricAmt(def, scoredCell, { missing });
        const present = metricCellScored(def, scoredCell, { missing }) || !!cell?.defaultPerfect;
        return {
          amt,
          pts: show === "total" ? (cell?.pts || 0) : (cell?.avgPts || 0),
          target: rawTargetOf(def),
          present,
          hasDef: !!def,
          scored: present,
          def,
          workspaceId: w.id,
          scorers: Number(cell?.scorers) || 0,
        };
      });
      const amount = poolColumn(cells.map((c) => ({ amt: c.amt, present: c.present })), col.mode);
      const points = poolColumn(cells.map((c) => ({ amt: c.pts, present: c.present })), col.mode);
      /* Targets come from the KPI defs (both workspaces), not only teams that
         already scored — otherwise one side's $2k monthly target owned the card. */
      const rawTarget = poolColumn(
        cells.filter((c) => c.hasDef && c.target > 0).map((c) => ({ amt: c.target, present: true })),
        col.mode,
      );
      const base = repDef || {};
      const kind = kindOfKpi(base, col.kind);
      const daily = rawTarget > 0 ? rawTarget : rawTargetOf(base);
      /* Each workspace's own source metric for this column — a Custom plan
         pairs differently-named (or differently-configured) metrics per
         workspace on purpose, so `base`/`repDef` is only ONE of them. The
         info tooltip needs every source's own benchmark/points rule to
         speak accurately instead of only for whichever source came first. */
      const perWorkspace = cells
        .filter((c) => c.hasDef && c.def)
        .map((c) => ({ workspaceId: c.workspaceId, workspaceName: wsNameById[c.workspaceId] || "Workspace", def: c.def, scorers: c.scorers }));
      return {
        ...base,
        _id: col.key,
        title: col.label,
        kind,
        amount,
        points,
        _dailyTarget: daily,
        target: daily,
        _sourceIds: cells
          .map((_, i) => col.sources?.[String(perWs[i].id)])
          .filter(Boolean)
          .map(String),
        _perWorkspace: perWorkspace,
        _workspaceCount: perWs.length,
      };
    };

    const planCols = hasMetricPlan(plan) ? effectiveMetricColumns(perWs, plan, true) : null;
    const kpis = planCols ? planCols.map(shapeColumn) : shared.map(shapeKpi);
    const kpisAll = sharedAll.map(shapeKpi);

    /* The Yeti's pace over the same metrics / same [from, to] as teamPts.
       Build ONE Yeti per workspace, then avg/total those paces the same way
       teamPts combines workspaces. Flattening every workspace's KPI defs into
       one buildYetiScoresForKpis call summed Field+In-House (~16.6k) while the
       team climb averaged (~7.4k) — nonsense on Head of Sales "All". */
    const { monthRef: yetiMonthRef, asOf: yetiAsOf, range: yetiRange } = yetiInputsForWindow(fetchWin, from, to);
    /* Keep each part next to the workspace it was built for as well as in the
       flat list `combinePeriod` folds. A per-PLAYER question ("is this rep
       ahead of the Yeti?") has to read that rep's OWN team's pace — the
       combined `yeti` below is an average (or a sum) across the scope, which
       would measure an In-House rep against two teams' paces at once. */
    const yetiPartPairs = perWs
      .map((w) => (w.defs.length
        ? [String(w.id), buildYetiScoresForKpis(w.defs, yetiMonthRef, holidays, yetiAsOf, yetiRange)]
        : null))
      .filter(Boolean);
    const yetiParts = yetiPartPairs.map(([, part]) => part);
    const yetiByWs = Object.fromEntries(yetiPartPairs);
    /* The board's Yeti above only covers each workspace's ≤5 LIVE metrics. A
       Build Your Own tile can sit on any metric that has a target, so its
       comparison gets a second, per-metric pace over every metric with one.
       Each cell is computed independently, so a metric's pace is identical to
       what the live Yeti would give it — this only widens which metrics have
       one. Team totals (maxTotal) still come from the live set alone. */
    const yetiPaceByWs = Object.fromEntries(perWs
      .map((w) => {
        const withTarget = w.defsAll.filter((k) => Number(k.minimum || k.target) > 0);
        return withTarget.length
          ? [String(w.id), buildYetiScoresForKpis(withTarget, yetiMonthRef, holidays, yetiAsOf, yetiRange)]
          : null;
      })
      .filter(Boolean));
    const combinePeriod = (key) => {
      const cells = {};
      let totalSum = 0;
      let n = 0;
      yetiParts.forEach((part) => {
        const period = part[key];
        if (!period || typeof period !== "object") return;
        n += 1;
        totalSum += Number(period.total) || 0;
        Object.entries(period).forEach(([id, cell]) => {
          if (id === "total") return;
          cells[id] = cell;
        });
      });
      if (!n) return null;
      const total = show === "total" ? totalSum : totalSum / n;
      return { ...cells, total };
    };
    const raw = yetiParts.length ? {
      today: combinePeriod("today"),
      yesterday: combinePeriod("yesterday"),
      week: combinePeriod("week"),
      lastWeek: combinePeriod("lastWeek"),
      month: combinePeriod("month"),
      custom: combinePeriod("custom"),
      maxTotal: (() => {
        const sum = yetiParts.reduce((n, p) => n + (Number(p.maxTotal) || 0), 0);
        return show === "total" ? sum : sum / yetiParts.length;
      })(),
    } : null;
    const rangePeriod = windowUsesRangeYeti(fetchWin) ? (raw?.custom || null) : null;
    const yeti = raw
      ? {
        today: raw.today,
        yesterday: raw.yesterday,
        week: raw.week,
        lastWeek: raw.lastWeek,
        /* Keep `.month` as the active window for consumers that still read it
           (ScopedCard, older Beat-the-Yeti paths) when the board is on a
           range window — otherwise they silently stay on MTD. */
        month: rangePeriod || raw.month,
        custom: raw.custom,
        maxTotal: raw.maxTotal || 0,
        /* Business-days-in-month count — every scoped workspace shares the
           same company calendar/holidays, so any part's totalDays is the
           right one. Omitting this left MiniChart's `amt * yeti.totalDays`
           at NaN for combined cards, pinning its "Yeti" marker to the chart's
           start regardless of the real pace. */
        totalDays: yetiParts[0]?.totalDays || 0,
      }
      : null;

    /* Plan columns use synthetic _id keys; Yeti pace is keyed by real KPI ids.
       Alias each column key to the average of its source KPI paces so deltas
       aren't "full amount vs $0 Yeti". */
    if (yeti && planCols) {
      const yetiKey = yetiBucketForWindow(fetchWin === "year" ? "ytd" : fetchWin);
      planCols.forEach((col, i) => {
        const shaped = kpis[i];
        const srcIds = (shaped && shaped._sourceIds) || Object.values(col.sources || {}).map(String);
        if (!srcIds.length) return;
        let amtSum = 0;
        let ptsSum = 0;
        let n = 0;
        srcIds.forEach((sid) => {
          const cell = yeti[yetiKey]?.[sid] ?? yeti.month[sid];
          if (cell == null) return;
          n += 1;
          if (typeof cell === "number") {
            amtSum += cell;
            ptsSum += cell;
          } else {
            amtSum += Number(cell.amt) || 0;
            ptsSum += Number(cell.pts) || 0;
          }
        });
        if (n) {
          const pace = { amt: amtSum / n, pts: ptsSum / n };
          yeti.month[col.key] = pace;
          if (yeti[yetiKey] && yetiKey !== "month") yeti[yetiKey][col.key] = pace;
        }
      });
    }

    /* Everyone in scope, for the "Players" filter and the leaderboard. */
    const people = [];
    perWs.forEach((w) => {
      w.players.forEach((p) => {
        const u = usersDict?.[p.userId] || usersDict?.[String(p.userId)] || {};
        if (!isLiveBoardPlayer(usersDict, p.userId)) return;
        people.push({
          userId: p.userId,
          wsId: w.id,
          name: [u.firstName, u.lastName].filter(Boolean).join(" ") || u.name || "Player",
          image: u.image || "",
          pts: p.pts,
          kpis: p.kpis,
        });
      });
    });

    /* The climber's height on the mountain: the FULL points total per player,
       exactly what the leaderboard and Gameboard show — the sum of every KPI's
       points, not just the ≤5 live tiles. `readWorkspace` already accumulates
       each player's full pts; here we average (or total) across the scope.

       An earlier version summed only the live-metric points, which undercut the
       climb to a fraction of the real average (1,600 where the leaderboard read
       6,270). Reading the full per-player total is what makes the Mountain agree
       with the board around it. */
    let teamPts;
    if (show === "total") {
      teamPts = perWs.reduce((n, w) => n + w.teamPts, 0);
    } else {
      const perWsAvg = perWs.map((w) => (w.players.length ? w.teamPts / w.players.length : 0));
      teamPts = perWsAvg.length ? perWsAvg.reduce((a, b) => a + b, 0) / perWsAvg.length : 0;
    }

    /* Daily series for CustomWidget ▲/▼ (and spark graphs) — half-vs-half of
       the SELECTED window, not only the month of `to`. Stitch every month the
       score fetch already loaded so Last week / YTD / custom ranges work. */
    let seriesByDay = null;
    const liveScores = [];
    ids.forEach((wsId) => {
      (scoresByWs[wsId] || []).forEach((score) => {
        const ownerId = score.ownerId || score.userId;
        if (!isLiveBoardPlayer(usersDict, ownerId)) return;
        liveScores.push(score);
      });
    });
    if (liveScores.length && from && to && from.isValid() && to.isValid()) {
      const total = {};
      const cursor = from.clone().startOf("month");
      const lastMonth = to.clone().startOf("month");
      while (cursor.isSameOrBefore(lastMonth, "month")) {
        Object.assign(total, buildCompanyScoresByDay(liveScores, cursor));
        cursor.add(1, "month");
      }
      if (show === "total") {
        seriesByDay = total;
      } else {
        const activeCount = new Set(
          liveScores.map((s) => String(s.ownerId || s.userId)).filter(Boolean),
        ).size || 1;
        seriesByDay = {};
        Object.entries(total).forEach(([day, obj]) => {
          seriesByDay[day] = {};
          Object.entries(obj || {}).forEach(([k, v]) => {
            if (k === "total") {
              seriesByDay[day].total = (Number(v) || 0) / activeCount;
            } else {
              seriesByDay[day][k] = {
                amt: (Number(v?.amt) || 0) / activeCount,
                pts: (Number(v?.pts) || 0) / activeCount,
              };
            }
          });
        });
      }
      /* Plan columns use synthetic ids — alias each column key to the avg of
         its source KPI day amounts so MetricBlock can look them up. */
      if (planCols && seriesByDay) {
        Object.keys(seriesByDay).forEach((day) => {
          const bucket = seriesByDay[day];
          if (!bucket) return;
          planCols.forEach((col) => {
            const srcIds = Object.values(col.sources || {}).map(String).filter(Boolean);
            if (!srcIds.length) return;
            let amtSum = 0;
            let ptsSum = 0;
            let n = 0;
            srcIds.forEach((sid) => {
              const cell = bucket[sid];
              if (cell == null) return;
              n += 1;
              if (typeof cell === "number") {
                amtSum += cell;
                ptsSum += cell;
              } else {
                amtSum += Number(cell.amt) || 0;
                ptsSum += Number(cell.pts) || 0;
              }
            });
            if (n) {
              bucket[col.key] = { amt: amtSum / n, pts: ptsSum / n };
            }
          });
        });
      }
      /* Also alias by metric title / lowercase name — Analytics cards often
         persist the catalog title as metricId, not the Mongo id. */
      perWs.forEach((w) => {
        (w.defsAll || []).forEach((def) => {
          if (!def || !def._id) return;
          const realId = String(def._id);
          const aliases = [
            nameKey(def),
            (def.title || def.name || "").trim(),
            (def.title || def.name || "").trim().toLowerCase(),
          ].filter(Boolean);
          Object.keys(seriesByDay).forEach((day) => {
            const bucket = seriesByDay[day];
            const cell = bucket && bucket[realId];
            if (cell == null) return;
            aliases.forEach((a) => {
              if (!bucket[a]) bucket[a] = cell;
            });
          });
        });
      });
    }

    /* `yetiWin` is the window `yetiByWs` was actually built for (fetchWin, which
       a metric-plan card widens past the card's own window) — a consumer must
       only read a pace off it when that matches the window it is drawing. */
    return { kpis, kpisAll, yeti, yetiByWs, yetiPaceByWs, yetiWin: fetchWin === "year" ? "ytd" : fetchWin, people, teamPts, from, to, seriesByDay, loading, refreshing, workspaceCount: ids.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, companyKpis, canFetch, defsReady, rosterReady, scoresByWs, windowRatesByWs, win, fetchWin, customKey, show, planKey, holidays, usersDict, loading, refreshing, wsNameById]);
}
